import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { TrendingUp, Brain, Shield, Zap, BarChart3, Sparkles } from "lucide-react";
import Footer from "@/components/Footer";

export default function Landing() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI-Powered Predictions",
      description: "Advanced machine learning models analyze market trends in real-time"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Multi-Asset Coverage",
      description: "Track stocks, cryptocurrencies, and emerging market opportunities"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Smart Analytics",
      description: "Deep insights with historical data and predictive analytics"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Risk Assessment",
      description: "Comprehensive risk analysis for informed decision-making"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Real-Time Updates",
      description: "Live market data and instant prediction updates"
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Learning Hub",
      description: "Educational content for beginners to advanced traders"
    }
  ];

  return (
    <div className="min-h-screen bg-[#030712] relative overflow-hidden">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-transparent to-teal-900/10" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00E0FF]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#6366F1]/5 rounded-full blur-[120px]" />

      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 border-b border-white/5 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="/logo192.png" alt="TradeInsights logo" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg shrink-0" data-testid="landing-logo" />
            <span className="text-base sm:text-xl md:text-2xl font-bold tracking-tight text-white truncate" style={{ fontFamily: 'Unbounded' }}>
              TRADE<span className="text-[#00E0FF]">INSIGHTS</span>
            </span>
          </div>
          <Button 
            data-testid="nav-dashboard-btn"
            onClick={() => navigate('/dashboard')} 
            className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold px-4 sm:px-6 shrink-0 shadow-[0_0_20px_rgba(0,224,255,0.3)] hover:shadow-[0_0_30px_rgba(0,224,255,0.5)] transition-all duration-300"
          >
            <span className="hidden sm:inline">Launch Dashboard</span>
            <span className="sm:hidden">Dashboard</span>
          </Button>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center space-y-8"
        >
          <div className="inline-block">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-[#00E0FF]" />
              <span className="text-sm text-gray-400 font-medium">AI-Powered Market Intelligence</span>
            </div>
          </div>
          
          <h1 
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none text-white"
            style={{ fontFamily: 'Unbounded' }}
          >
            PREDICT THE
            <br />
            <span className="bg-gradient-to-r from-[#00E0FF] via-[#6366F1] to-[#00FFA3] bg-clip-text text-transparent">
              MARKET'S NEXT MOVE
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Harness cutting-edge AI to predict stock and cryptocurrency trends.
            Make data-driven investment decisions with confidence.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button 
              data-testid="hero-get-started-btn"
              onClick={() => navigate('/dashboard')} 
              size="lg"
              className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-bold text-lg px-8 py-6 shadow-[0_0_30px_rgba(0,224,255,0.4)] hover:shadow-[0_0_40px_rgba(0,224,255,0.6)] transition-all duration-300 hover:-translate-y-1"
            >
              Get Started Free
            </Button>
            <Button 
              data-testid="hero-learn-more-btn"
              onClick={() => navigate('/learning')} 
              size="lg"
              variant="outline"
              className="border-[#00E0FF]/50 text-[#00E0FF] hover:bg-[#00E0FF]/10 font-semibold text-lg px-8 py-6 transition-all duration-300 hover:-translate-y-1"
            >
              Learn More
            </Button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-24"
        >
          {[
            { label: "Prediction Accuracy", value: "94%" },
            { label: "Assets Tracked Live", value: "100" },
            { label: "AI Signal Monitoring", value: "24/7" }
          ].map((stat) => (
            <div 
              key={stat.label}
              className="glass-effect rounded-xl p-6 text-center hover:border-[#00E0FF]/30 transition-all duration-300 hover:-translate-y-1"
            >
              <div 
                className="text-4xl font-bold text-[#00E0FF] mb-2" 
                style={{ fontFamily: 'JetBrains Mono' }}
              >
                {stat.value}
              </div>
              <div className="text-gray-400 text-sm uppercase tracking-widest">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Features Grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-32">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 
            className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4"
            style={{ fontFamily: 'Unbounded' }}
          >
            POWERFUL FEATURES
          </h2>
          <p className="text-gray-400 text-lg">Everything you need to dominate the markets</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="glass-effect rounded-xl p-8 hover:border-[#00E0FF]/30 transition-all duration-300 hover:-translate-y-2 group"
            >
              <div className="w-16 h-16 bg-[#00E0FF]/10 rounded-lg flex items-center justify-center mb-6 text-[#00E0FF] group-hover:bg-[#00E0FF]/20 transition-colors duration-300">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded' }}>
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="glass-effect rounded-2xl p-12 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#00E0FF]/10 to-[#6366F1]/10" />
          <div className="relative z-10">
            <h2 
              className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6"
              style={{ fontFamily: 'Unbounded' }}
            >
              READY TO START TRADING SMARTER?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of traders using AI to make better investment decisions
            </p>
            <Button 
              data-testid="cta-start-btn"
              onClick={() => navigate('/dashboard')} 
              size="lg"
              className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-bold text-lg px-10 py-6 shadow-[0_0_30px_rgba(0,224,255,0.4)] hover:shadow-[0_0_40px_rgba(0,224,255,0.6)] transition-all duration-300 hover:-translate-y-1"
            >
              Start Predicting Now
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
